import React, { useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import {
  faCaretRight,
  faClose,
  faCreditCard,
  faFile,
  faMoneyBill1,
  faMoneyBillTransfer,
  faMoneyBillWheat,
  faPlus,
  faRemove,
  faSignature,
  faTable,
  faUserEdit,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { TitleChildrenPath, TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { CardEmployeeRegisterInLine } from "../../../components/CardEmployeeRegister/CardEmployeeRegister"
import InputField from "../../../components/InputField/InputField"
import { useSaveOrder } from "../../../hooks/Order/useSaveOrder"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useEmployerLogged } from "../../../context/useEmployeeContext"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import { ECustomerPaymentType } from "../../../Enum/ECustomerPaymentType"
import { ListSelections, type ListSelectionsProbs } from "../../../components/ListSelections/ListSelections"
import { orderMemory } from "../../../Objects/Memory/Memory"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { CardItem } from "../../../components/Card/CardItem"
import { BasicCard } from "../../../components/BasicCard/BasicCard"
import { useTranslation } from "react-i18next"
import i18next from "i18next"
import { CountElements } from "../../../components/CountElements/CountElements"
import { CalculateView } from "./Components/CalculateView"
import { EOrderPlaceType } from "../../../Enum/EOrderPlaceType"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { EPaymentType } from "../../../Enum/EPaymentType"

const renderProviderChargeReference = (externalReference?: string) => {
  if (!externalReference) {
    return null
  }

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
      <span className="font-semibold">{i18next.t("order:label_provider_external_reference")} </span>

      <span className="break-all font-mono">{externalReference}</span>
    </div>
  )
}

interface DetailsPaymentPupopProbs {
  order: OrderEntity
  onSave: () => void
  onClonse: () => void
}

export const DetailsPaymentPupop: React.FC<DetailsPaymentPupopProbs> = ({ onSave, order, onClonse }) => {
  const { t } = useTranslation("order")

  const { setNewConfirmation } = useConfirmation()

  const [ordersPaidState, setOrdersPaidState] = useState<OrderEntity[]>([])

  const [orderFinal] = useState<OrderEntity>(order)

  const [totalFinal, setTotal] = useState<number>(0)

  const [dataListSelected, setListSelect] = useState<ListSelectionsProbs>()

  const [enableList, setEnableList] = useState(false)

  const { userEmployee } = useEmployerLogged()

  const { handleSaveOrder } = useSaveOrder()

  const [details, setDetails] = useState(order.orderPaymentEntity.description)

  const [paymentType, setPaymentType] = useState<EPaymentType>(
    order.orderPaymentEntity.ePaymentType ?? EPaymentType.None,
  )

  const [amountPaid, setAmountPaid] = useState(order.orderPaymentEntity.amountPaid?.toFixed(2) ?? "")

  const payableTotal = orderFinal.orderPaymentEntity.totalFinal + totalFinal

  const parsedAmountPaid = Number(amountPaid)

  const changeAmount =
    paymentType === EPaymentType.Cash && Number.isFinite(parsedAmountPaid)
      ? Math.max(0, parsedAmountPaid - payableTotal)
      : 0

  const hasValidCashAmount =
    amountPaid.trim().length === 0 || (Number.isFinite(parsedAmountPaid) && parsedAmountPaid >= payableTotal)

  const canConfirmPayment =
    orderFinal.orderPaymentEntity.isOnlinePayment ||
    paymentType === EPaymentType.None ||
    paymentType !== EPaymentType.Cash ||
    hasValidCashAmount

  const handleSubmiteSave = async (overridePaymentType?: EPaymentType) => {
    const effectivePaymentType = overridePaymentType ?? paymentType

    orderFinal.orderPaymentEntity.description = details

    orderFinal.orderPaymentEntity.status = ECustomerPaymentType.WithOutDebit

    if (!orderFinal.orderPaymentEntity.isOnlinePayment) {
      orderFinal.orderPaymentEntity.ePaymentType = effectivePaymentType
      orderFinal.orderPaymentEntity.amountPaid =
        effectivePaymentType === EPaymentType.Cash ? parsedAmountPaid : undefined
    }

    orderFinal.ordersPaidIds = ordersPaidState.map((e) => e.id)

    await handleSaveOrder(orderFinal, onSave, false, true)
  }

  const handleChangeDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
    orderFinal.orderPaymentEntity.description = e.target.value

    setDetails(e.target.value)
  }

  const handleRemoveAssociete = (id: number) => {
    setOrdersPaidState((prev) => {
      const next = prev.filter((e) => e.id !== id)
      setTotal(next.reduce((acc, orderItem) => acc + orderItem.orderPaymentEntity.totalFinal, 0))
      return next
    })
  }

  const handleAssociete = (id: number) => {
    setEnableList(false)

    if (ordersPaidState.find((e) => e.id === id)) {
      return
    }

    const orderTemp = orderMemory.findById(id)
    if (!orderTemp) {
      return
    }

    setOrdersPaidState((prev) => {
      const newList = [...prev, orderTemp]

      const newTotal = newList.reduce((acc, o) => acc + o.orderPaymentEntity.totalFinal, 0)

      setTotal(newTotal)

      return newList
    })
  }

  const handleOpenAddOrder = () => {
    setListSelect({
      icon: faFile,
      title: t("title_command_select"),
      messageCount: t("list:label_order_page_message_count"),
      messageEmpty: t("list:label_order_page_message_empty"),
      onClinck: (e: any) => handleAssociete(e),
      onClonse: () => setEnableList(false),
      listCards: orderMemory.getListPaidCards(orderFinal.id),
    })
    setEnableList(true)
  }

  return (
    <div className={defaultStyle.overlay}>
      <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
        <div className={defaultStyle.alert}>
          <TitlePageResponsiveIconSmall
            iconColor={THEME.colors.iconBGColor}
            title={t("title_payment_command")}
            icon={faCreditCard}
            children={
              <div className={defaultStyle.ButtonsActions}>
                <PopupHelpButton
                  helpKey="pages.fluxo.order.detailspaymentpupop.1"
                  variant="global"
                  title={t("helpPopup:global.command.title")}
                  description={t("helpPopup:global.command.description")}
                  bullets={t("helpPopup:global.command.bullets", { returnObjects: true }) as string[]}
                />

                <GetSpace right={5} />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  icon={faClose}
                  typeButton="button"
                  size="small"
                  onClick={onClonse}
                  descriptionKey="interactionDescriptions:pages.fluxo.order.detailsPaymentPupop.button.onClonse"
                />
              </div>
            }
          />

          <GetSpace top={5} />

          <>
            <CardEmployeeRegisterInLine
              icon={faUserEdit}
              iconColor={THEME.colors.iconBGSecondaryColor}
              date={orderFinal.date}
              employee={userEmployee!}
            />

            <GetSpace top={5} />

            <TitleChildrenPath
              title={orderFinal.getCustomerDisplayName()}
              icon={faSignature}
              iconColor="black"
              boxShadow="none"
              keyCode={"Costumer-payment"}
              subtitleChildren={
                <div style={{ paddingRight: "10px" }}>
                  <BasicCard borderColor={THEME.colors.white} title={orderFinal.getCustomerDisplayCpf()} />
                </div>
              }
            />

            <TitleChildrenPath
              title={t("components:label_tables")}
              iconColor={THEME.colors.iconBGAlertColor}
              icon={faTable}
              boxShadow="none"
              subtitleChildren={
                <div className="flex items-center gap-2">
                  {orderFinal.tableEntity && (
                    <div style={{ paddingRight: "15px" }}>
                      <BasicCard
                        borderColor={THEME.colors.white}
                        title={orderFinal.tableEntity?.number === 0 ? "0" : orderFinal.tableEntity?.number.toString()}
                      />
                    </div>
                  )}
                </div>
              }
              children={
                <div className={"p-2"}>
                  <GetSpace top={5} />

                  <InputField
                    type="text"
                    maxLength={INPUT_SIZE.ORDER.genericMealDetails}
                    placeholder={t("general:label_observation")}
                    icon={faFile}
                    value={details}
                    initialValue={orderFinal.orderPaymentEntity.description}
                    onChange={handleChangeDetails}
                    descriptionKey="interactionDescriptions:pages.fluxo.order.detailsPaymentPupop.input.observation"
                  />
                </div>
              }
            />
          </>

          {orderFinal.orderPaymentEntity.isOnlinePayment && (
            <>
              <div className={defaultStyle.divisionBar}></div>
              {renderProviderChargeReference(orderFinal.orderPaymentEntity.externalReference)}
            </>
          )}

          {orderFinal.eOrderPlaceType === EOrderPlaceType.Local && (
            <>
              <div className={defaultStyle.divisionBar}></div>
              <TitleChildrenPath
                iconColor={THEME.colors.iconBGColor}
                subtitle={t("label_pay_another_command")}
                icon={faUsers}
                boxShadow="none"
                subtitleChildren={
                  <div className="flex items-center gap-2">
                    <PopupHelpButton
                      helpKey="pages.fluxo.order.detailspaymentpupop.2"
                      title={t("helpPopup:sections.command.associatedCommands.title")}
                      description={t("helpPopup:sections.command.associatedCommands.description")}
                      bullets={
                        t("helpPopup:sections.command.associatedCommands.bullets", { returnObjects: true }) as string[]
                      }
                    />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonExpandIcon}
                      bgColor={THEME.colors.buttonBGPlusColor}
                      icon={faPlus}
                      typeButton="button"
                      size="small"
                      onClick={handleOpenAddOrder}
                      descriptionKey="interactionDescriptions:pages.fluxo.order.detailsPaymentPupop.button.handleOpenAddOrder"
                    />
                  </div>
                }
                children={
                  <>
                    <CountElements count={ordersPaidState.length} title={t("label_commands_dots")} boxShadow={"none"} />

                    {ordersPaidState.map((e) => {
                      return (
                        <>
                          <GetSpace top={5} />

                          <TitlePageResponsiveIconSmall
                            boxShadow="none"
                            subtitleChildren={
                              <>
                                <BasicCard
                                  borderColor={THEME.colors.white}
                                  title={convertToLocalMoney(e.orderPaymentEntity.totalFinal)}
                                />

                                <GetSpace left={5} />

                                <ButtonIcon
                                  icon={faRemove}
                                  typeButton="button"
                                  size="small"
                                  sizeNumber={SIZE.buttonExpandIcon}
                                  bgColor={THEME.colors.buttonBGCloseColor}
                                  onClick={() => handleRemoveAssociete(e.id)}
                                  descriptionKey="interactionDescriptions:pages.fluxo.order.detailsPaymentPupop.button.handleRemoveAssocieteEId"
                                />
                              </>
                            }
                            icon={faCaretRight}
                            sizeProp="sm"
                            title={e.customerEntity.personEntity.name}
                          />
                        </>
                      )
                    })}
                  </>
                }
              />
            </>
          )}

          <div className={defaultStyle.divisionBar}></div>

          <CalculateView
            valueBaseMeals={orderFinal.orderPaymentEntity.totalMeals}
            valueBaseDrinks={orderFinal.orderPaymentEntity.totalDrinks}
            valueAddtionals={orderFinal.orderPaymentEntity.totalAddtionals}
            valueBaseSum={orderFinal.orderPaymentEntity.totalNotLiquid}
            valueTotalDiscount={orderFinal.orderPaymentEntity.totalDiscount}
            valueBaseSumWithDiscount={
              orderFinal.orderPaymentEntity.totalBase - orderFinal.orderPaymentEntity.totalDiscount
            }
            valueBaseExtraServices={orderFinal.orderPaymentEntity.totalExtra}
            valueTotalCoupons={orderFinal.orderPaymentEntity.totalCupom}
            valueTotalShippingFee={orderFinal.orderPaymentEntity.shippingFee}
            valueTotalAwards={orderFinal.orderPaymentEntity.totalAward}
            valueFinalWithoutAwards={orderFinal.orderPaymentEntity.totalFinal}
            valueFinal={orderFinal.orderPaymentEntity.totalFinal + (orderFinal.orderPaymentEntity.shippingFee ?? 0)}
            awards={orderFinal.associatedAwards}
            children={
              <>
                <div className={defaultStyle.divisionBar}></div>

                <CardItem
                  title={t("calculate:label_total_others_command")}
                  icon={faMoneyBillTransfer}
                  subtitle={convertToLocalMoney(totalFinal)}
                  iconColor={THEME.colors.black}
                  borderColor={THEME.colors.white}
                />

                <GetSpace top={5} />

                <CardItem
                  title={t("calculate:label_total_this_command")}
                  icon={faMoneyBill1}
                  subtitle={convertToLocalMoney(orderFinal.orderPaymentEntity.totalFinal)}
                  iconColor={THEME.colors.black}
                  borderColor={THEME.colors.white}
                />

                <GetSpace top={5} />

                <CardItem
                  title={t("calculate:label_total_final_with_all")}
                  icon={faMoneyBillWheat}
                  subtitle={convertToLocalMoney(
                    orderFinal.orderPaymentEntity.totalFinal +
                      totalFinal +
                      (orderFinal.orderPaymentEntity.shippingFee ?? 0),
                  )}
                  iconColor={THEME.colors.black}
                  borderColor={THEME.colors.black}
                />
              </>
            }
          />

          {!orderFinal.orderPaymentEntity.isOnlinePayment && (
            <Animate type={AnimationTypes.fadeInTraslate}>
              <>
                <div className={defaultStyle.divisionBar}></div>

                <section className="space-y-3 p-3">
                  <GenericDropDown
                    legend={t("label_payment_method")}
                    initialState={String(paymentType)}
                    data={[
                      { value: String(EPaymentType.None), label: t("label_payment_select") },
                      { value: String(EPaymentType.Cash), label: t("label_payment_cash") },
                      { value: String(EPaymentType.Pix), label: t("label_payment_pix") },
                      { value: String(EPaymentType.Card), label: t("label_payment_card") },
                      { value: String(EPaymentType.CreditCard), label: t("label_payment_credit_card") },
                      { value: String(EPaymentType.DebitCard), label: t("label_payment_debit_card") },
                      { value: String(EPaymentType.FoodVoucher), label: t("label_payment_food_voucher") },
                      { value: String(EPaymentType.MealVoucher), label: t("label_payment_meal_voucher") },
                      { value: String(EPaymentType.Other), label: t("label_payment_other") },
                    ]}
                    handlerFuncion={(event) => setPaymentType(Number(event.target.value) as EPaymentType)}
                    descriptionKey="interactionDescriptions:pages.fluxo.order.detailsPaymentPupop.select.paymentMethod"
                  />

                  {paymentType === EPaymentType.Cash && (
                    <>
                      <InputField
                        type="number"
                        value={amountPaid}
                        inputMode="decimal"
                        placeholder={t("label_amount_received")}
                        onChange={(event) => setAmountPaid(event.target.value)}
                        descriptionKey="interactionDescriptions:pages.fluxo.order.detailsPaymentPupop.input.amountReceived"
                      />

                      <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                        <span className="font-semibold">{t("label_change")}</span>

                        <strong>{convertToLocalMoney(changeAmount)}</strong>
                      </div>

                      {!canConfirmPayment && amountPaid.length > 0 && (
                        <p className="text-xs font-semibold text-red-600">{t("message_amount_received_too_low")}</p>
                      )}
                    </>
                  )}
                </section>
              </>
            </Animate>
          )}

          <div className={defaultStyle.divisionBar}></div>

          <div className={defaultStyle.buttonsController}>
            <Button
              label={t("buttons:button_close_and_pay")}
              typeButton="button"
              type="primary"
              size="medium"
              bgColor={THEME.colors.buttonBGCloseColor}
              disabled={!canConfirmPayment}
              onClick={() =>
                setNewConfirmation(t("title_payment_command"), t("title_path_agree_close"), () =>
                  handleSubmiteSave(),
                )
              }
              descriptionKey="interactionDescriptions:pages.fluxo.order.detailsPaymentPupop.button.closeAndPay"
            />
          </div>
        </div>
      </Animate>

      {enableList && (
        <ListSelections
          title={dataListSelected!.title}
          icon={dataListSelected!.icon}
          listCards={dataListSelected!.listCards}
          messageCount={dataListSelected!.messageCount}
          messageEmpty={dataListSelected!.messageEmpty}
          onClinck={dataListSelected!.onClinck}
          onClonse={dataListSelected!.onClonse}
        />
      )}
    </div>
  )
}
